// GeneralIncomeDialog.js
// Dialog for adding general income (not tied to clients)

import React, { useState, useRef, useEffect } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { he } from 'date-fns/locale';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import MobileDialog from './MobileDialog';
import PaymentMethodRows from '../components/PaymentMethodRows';
import CreateTagDialog from './CreateTagDialog';
import {
  getGeneralTransactionTags,
  createGeneralTransaction,
} from '../../services/cashflowService';

const GeneralIncomeDialog = ({
  open,
  onClose,
  onSuccess,
}) => {
  // Form state
  const [selectedTagId, setSelectedTagId] = useState('');
  const [amount, setAmount] = useState('');
  const [actualDate, setActualDate] = useState(new Date());
  const [note, setNote] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);

  // UI state
  const [formStep, setFormStep] = useState('form');
  const [localError, setLocalError] = useState('');
  const [localSuccess, setLocalSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCreateTagDialog, setShowCreateTagDialog] = useState(false);
  const isSubmittingRef = useRef(false);
  const errorRef = useRef(null);

  const queryClient = useQueryClient();

  // Fetch income tags
  const { data: tags = [], isLoading: isLoadingTags, refetch: refetchTags } = useQuery({
    queryKey: ['general-transaction-tags', 'income'],
    queryFn: () => getGeneralTransactionTags('income'),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Scroll to error when it appears
  useEffect(() => {
    if (localError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [localError]);

  // Reset form state
  const resetForm = () => {
    setSelectedTagId('');
    setAmount('');
    setActualDate(new Date());
    setNote('');
    setPaymentMethods([]);
    setFormStep('form');
    setLocalError('');
    setLocalSuccess('');
  };

  // Handle form close
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Handle tag creation success
  const handleTagCreated = (newTag) => {
    refetchTags();
    setSelectedTagId(newTag.TagID);
    setShowCreateTagDialog(false);
  };

  // Validate form before moving to confirmation step
  const handleValidation = () => {
    setLocalError('');
    setLocalSuccess('');

    const parsedAmount = parseFloat(amount);
    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      setLocalError('שגיאה: הסכום חייב להיות גדול מאפס.');
      return;
    }

    // Validate payment methods if provided
    if (paymentMethods.length > 0) {
      const pmTotal = paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
      if (Math.abs(pmTotal - parsedAmount) > 0.01) {
        setLocalError('שגיאה: סכום אמצעי התשלום אינו תואם לסכום הכולל.');
        return;
      }

      // Check that all check payment methods have check number
      const invalidChecks = paymentMethods.filter(
        pm => pm.type === 'check' && !pm.check_number
      );
      if (invalidChecks.length > 0) {
        setLocalError('שגיאה: יש להזין מספר צ\'ק לכל תשלום בצ\'ק.');
        return;
      }
    }

    setFormStep('confirm');
  };

  // Submit income
  const handleSubmit = async () => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setLocalError('');
    setLocalSuccess('');

    try {
      const parsedAmount = parseFloat(amount);

      const payload = {
        type: 'income',
        tag_id: selectedTagId || null,
        amount: parsedAmount,
        actual_date: actualDate,
        note: note,
        payment_methods: paymentMethods.length > 0 ? paymentMethods.map(pm => ({
          type: pm.type,
          amount: parseFloat(pm.amount) || 0,
          check_number: pm.check_number || '',
          check_bank_name: pm.check_bank_name || '',
          check_account_number: pm.check_account_number || '',
          check_due_date: pm.check_due_date || null,
          card_last_four: pm.card_last_four || '',
          card_type: pm.card_type || '',
          bank_name: pm.bank_name || '',
          bank_account_last4: pm.bank_account_last4 || '',
          transfer_date: pm.transfer_date || null,
          notes: pm.notes || '',
        })) : [],
      };

      await createGeneralTransaction(payload);
      setLocalSuccess('ההכנסה נרשמה בהצלחה!');

      // Refetch queries immediately
      queryClient.refetchQueries({ queryKey: ['cashflow'] });
      queryClient.refetchQueries({ queryKey: ['cashflow-summary'] });
      queryClient.refetchQueries({ queryKey: ['general-transactions'] });

      if (onSuccess) {
        onSuccess();
      }

      // Close dialog after delay
      setTimeout(() => {
        resetForm();
        onClose();
      }, 1500);
    } catch (err) {
      console.error('שגיאה בהוספת הכנסה:', err);
      setLocalError(
        err?.response?.data?.error ||
        err.message ||
        'שגיאה בעת הוספת ההכנסה'
      );
    } finally {
      setIsSubmitting(false);
      isSubmittingRef.current = false;
    }
  };

  // Get selected tag name for confirmation
  const selectedTag = tags.find(t => t.TagID === selectedTagId);

  return (
    <>
      <MobileDialog
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            handleClose();
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '1.4rem' }}>
          הוספת הכנסה
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {localError && (
              <Alert ref={errorRef} severity="error">{localError}</Alert>
            )}
            {localSuccess && (
              <Alert severity="success">{localSuccess}</Alert>
            )}

            {formStep === 'form' && (
              <>
                {/* Tag Selection */}
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <FormControl fullWidth>
                    <InputLabel>קטגוריה</InputLabel>
                    <Select
                      value={selectedTagId}
                      onChange={(e) => setSelectedTagId(e.target.value)}
                      label="קטגוריה"
                      disabled={isLoadingTags}
                    >
                      <MenuItem value="">
                        <em>ללא קטגוריה</em>
                      </MenuItem>
                      {tags.map((tag) => (
                        <MenuItem key={tag.TagID} value={tag.TagID}>
                          {tag.Name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton
                    onClick={() => setShowCreateTagDialog(true)}
                    sx={{ mt: 1 }}
                    title="הוספת קטגוריה חדשה"
                  >
                    <AddIcon />
                  </IconButton>
                </Box>

                {/* Amount */}
                <TextField
                  label="סכום"
                  type="number"
                  fullWidth
                  sx={{
                    direction: 'ltr',
                    '& input': {
                      textAlign: 'center',
                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                        WebkitAppearance: 'none',
                        margin: 0,
                      },
                      '-moz-appearance': 'textfield',
                    },
                  }}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  onWheel={(e) => e.target.blur()}
                  onFocus={(e) => e.target.select()}
                />

                {/* Date */}
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={he}>
                  <DatePicker
                    label="תאריך"
                    value={actualDate}
                    onChange={(newValue) => setActualDate(newValue)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      }
                    }}
                  />
                </LocalizationProvider>

                {/* Note */}
                <TextField
                  label="הערות (אופציונלי)"
                  type="text"
                  multiline
                  minRows={2}
                  fullWidth
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />

                {/* Payment Methods */}
                <PaymentMethodRows
                  paymentMethods={paymentMethods}
                  setPaymentMethods={setPaymentMethods}
                  totalAmount={parseFloat(amount) || 0}
                  compact={false}
                />
              </>
            )}

            {formStep === 'confirm' && (
              <>
                <Typography sx={{ fontSize: '1.2rem', fontWeight: 'bold', mb: 1 }}>
                  אישור הכנסה
                </Typography>
                <Typography>סוג: הכנסה</Typography>
                {selectedTag && (
                  <Typography>קטגוריה: {selectedTag.Name}</Typography>
                )}
                <Typography sx={{ fontWeight: 'bold' }}>סכום: ₪{parseFloat(amount).toFixed(2)}</Typography>
                <Typography>תאריך: {actualDate?.toLocaleDateString('he-IL')}</Typography>
                {note && (
                  <Typography>הערות: {note}</Typography>
                )}

                {/* Show payment methods */}
                {paymentMethods.length > 0 && (
                  <>
                    <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                      אמצעי תשלום:
                    </Typography>
                    {paymentMethods.map((pm, index) => {
                      const typeLabels = {
                        cash: 'מזומן',
                        check: 'צ\'ק',
                        credit_card: 'כרטיס אשראי',
                        bank_transfer: 'העברה בנקאית',
                        other: 'אחר'
                      };
                      return (
                        <Typography key={index}>
                          {typeLabels[pm.type] || pm.type}: ₪{parseFloat(pm.amount).toFixed(2)}
                        </Typography>
                      );
                    })}
                  </>
                )}
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions>
          {!localSuccess && (
            <Button
              onClick={handleClose}
              color="inherit"
              size="large"
              sx={{ fontSize: '1rem' }}
            >
              ביטול
            </Button>
          )}

          {formStep === 'form' && !localSuccess && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              sx={{ fontSize: '1rem' }}
              onClick={handleValidation}
            >
              הבא
            </Button>
          )}

          {formStep === 'confirm' && !localSuccess && (
            <>
              <Button
                color="inherit"
                size="large"
                sx={{ fontSize: '1rem' }}
                onClick={() => setFormStep('form')}
              >
                חזרה
              </Button>
              <Button
                variant="contained"
                color="success"
                size="large"
                sx={{ fontSize: '1rem' }}
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <CircularProgress size={24} />
                ) : (
                  'אישור'
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </MobileDialog>

      {/* Create Tag Dialog */}
      <CreateTagDialog
        open={showCreateTagDialog}
        onClose={() => setShowCreateTagDialog(false)}
        onSuccess={handleTagCreated}
        defaultType="income"
      />
    </>
  );
};

export default GeneralIncomeDialog;
